import { runMonteCarloSimulation } from '../../simulation/engine';
import type {
  DistributionConfig,
  DistributionNodeData,
  SimulationGraph,
  SimulationNode
} from '../../../models/simulation';

export type QuantileSet = {
  p10: number;
  median: number;
  p90: number;
};

export type InputAssumptionRow = {
  id: string;
  node: SimulationNode & { data: DistributionNodeData };
  name: string;
  tags: string[];
  unit: string;
  quantiles: QuantileSet;
  impactDelta: number | null;
  impactMagnitude: number;
  impactUnit?: string;
  impactLowValue?: number;
  impactHighValue?: number;
  impactError?: string;
};

export type SensitivityTarget = {
  id: string;
  name: string;
  isOutput: boolean;
};

export type TornadoResult = {
  rows: InputAssumptionRow[];
  baselineValue: number | null;
  baselineUnit?: string;
  baselineError?: string;
};

const clamp = (value: number, min?: number, max?: number): number => {
  let nextValue = value;
  if (Number.isFinite(min)) {
    nextValue = Math.max(nextValue, min!);
  }
  if (Number.isFinite(max)) {
    nextValue = Math.min(nextValue, max!);
  }
  return nextValue;
};

const triangularQuantile = (min: number, mode: number, max: number, p: number): number => {
  if (max === min) {
    return min;
  }

  const clampedMode = clamp(mode, min, max);
  const split = (clampedMode - min) / (max - min);
  if (p < split) {
    return min + Math.sqrt(p * (max - min) * (clampedMode - min));
  }

  return max - Math.sqrt((1 - p) * (max - min) * (max - clampedMode));
};

export const assumptionQuantiles = (config: DistributionConfig): QuantileSet => {
  switch (config.kind) {
    case 'fixed':
      return { p10: config.value, median: config.value, p90: config.value };
    case 'normal':
      return {
        p10: config.mean - config.std * 1.281551565545,
        median: config.mean,
        p90: config.mean + config.std * 1.281551565545
      };
    case 'triangular':
      return {
        p10: triangularQuantile(config.min, config.mode, config.max, 0.1),
        median: triangularQuantile(config.min, config.mode, config.max, 0.5),
        p90: triangularQuantile(config.min, config.mode, config.max, 0.9)
      };
    case 'uniform':
      return {
        p10: config.min + (config.max - config.min) * 0.1,
        median: config.min + (config.max - config.min) / 2,
        p90: config.min + (config.max - config.min) * 0.9
      };
  }
};

export const findPrimaryOutputNode = (graph: SimulationGraph): SimulationNode | undefined =>
  graph.nodes.find((node) => node.data.isOutput === true)
  ?? [...graph.nodes].reverse().find((node) => node.data.type === 'formula');

export const getSensitivityTargets = (graph: SimulationGraph): SensitivityTarget[] =>
  graph.nodes
    .filter((node) => node.data.isOutput === true || node.data.type === 'formula')
    .map((node) => ({
      id: node.id,
      name: node.data.name,
      isOutput: node.data.isOutput === true
    }));

const buildFixedGraph = (graph: SimulationGraph, fixedValues: Map<string, number>): SimulationGraph => ({
  ...graph,
  nodes: graph.nodes.map((node) => {
    if (node.data.type !== 'distribution') {
      return node;
    }

    return {
      ...node,
      data: {
        ...node.data,
        config: {
          kind: 'fixed',
          value: fixedValues.get(node.id) ?? assumptionQuantiles(node.data.config).median,
          label: 'Fixed'
        }
      }
    };
  })
});

const evaluateDeterministicTarget = (
  graph: SimulationGraph,
  targetId: string
): { value: number; unit?: string } => {
  const result = runMonteCarloSimulation(graph, {
    trials: 1,
    seed: 1,
    collectSamples: false
  });
  const targetResult = result.results[targetId];
  if (!targetResult || !Number.isFinite(targetResult.summary.mean)) {
    throw new Error('Selected target did not produce a finite value.');
  }

  return {
    value: targetResult.summary.mean,
    unit: targetResult.unit
  };
};

const calculateTornadoImpact = (
  graph: SimulationGraph,
  targetId: string | undefined,
  inputNodeId: string,
  quantilesByNodeId: Map<string, QuantileSet>,
  medianValues: Map<string, number>
): Pick<InputAssumptionRow, 'impactDelta' | 'impactMagnitude' | 'impactUnit' | 'impactLowValue' | 'impactHighValue' | 'impactError'> => {
  if (!targetId) {
    return {
      impactDelta: null,
      impactMagnitude: 0,
      impactError: 'No sensitivity target is selected.'
    };
  }

  const inputQuantiles = quantilesByNodeId.get(inputNodeId);
  if (!inputQuantiles) {
    return {
      impactDelta: null,
      impactMagnitude: 0,
      impactError: 'Input quantiles are unavailable.'
    };
  }

  try {
    const lowValues = new Map(medianValues);
    lowValues.set(inputNodeId, inputQuantiles.p10);
    const highValues = new Map(medianValues);
    highValues.set(inputNodeId, inputQuantiles.p90);

    const lowResult = evaluateDeterministicTarget(buildFixedGraph(graph, lowValues), targetId);
    const highResult = evaluateDeterministicTarget(buildFixedGraph(graph, highValues), targetId);
    const delta = highResult.value - lowResult.value;

    return {
      impactDelta: delta,
      impactMagnitude: Math.abs(delta),
      impactUnit: highResult.unit ?? lowResult.unit,
      impactLowValue: lowResult.value,
      impactHighValue: highResult.value
    };
  } catch (error) {
    return {
      impactDelta: null,
      impactMagnitude: 0,
      impactError: error instanceof Error ? error.message : String(error)
    };
  }
};

export const buildTornadoResult = (
  graph: SimulationGraph,
  targetId?: string
): TornadoResult => {
  const sensitivityTargets = getSensitivityTargets(graph);
  const target =
    (targetId ? sensitivityTargets.find((candidate) => candidate.id === targetId) : undefined)
    ?? sensitivityTargets.find((candidate) => candidate.id === findPrimaryOutputNode(graph)?.id)
    ?? sensitivityTargets[0];

  const quantilesByNodeId = new Map<string, QuantileSet>();
  graph.nodes.forEach((node) => {
    if (node.data.type === 'distribution') {
      quantilesByNodeId.set(node.id, assumptionQuantiles(node.data.config));
    }
  });

  const medianValues = new Map<string, number>();
  quantilesByNodeId.forEach((quantiles, nodeId) => {
    medianValues.set(nodeId, quantiles.median);
  });

  let baselineValue: number | null = null;
  let baselineUnit: string | undefined;
  let baselineError: string | undefined;
  if (target?.id) {
    try {
      const baselineResult = evaluateDeterministicTarget(buildFixedGraph(graph, medianValues), target.id);
      baselineValue = baselineResult.value;
      baselineUnit = baselineResult.unit;
    } catch (error) {
      baselineError = error instanceof Error ? error.message : String(error);
    }
  }

  const rows = graph.nodes
    .filter((node): node is SimulationNode & { data: DistributionNodeData } => node.data.type === 'distribution')
    .map((node) => {
      const quantiles = quantilesByNodeId.get(node.id) ?? assumptionQuantiles(node.data.config);
      const tags = node.data.tags ?? [];
      const unit = node.data.unit ?? '';
      const impact = baselineError
        ? {
            impactDelta: null,
            impactMagnitude: 0,
            impactError: baselineError
          }
        : calculateTornadoImpact(graph, target?.id, node.id, quantilesByNodeId, medianValues);

      return {
        id: node.id,
        node,
        name: node.data.name,
        tags,
        unit,
        quantiles,
        ...impact
      };
    });

  return {
    rows,
    baselineValue,
    baselineUnit,
    baselineError
  };
};
